import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import {
  LayoutDashboard, 
  Users, 
  Calendar, 
  Clock, 
  LogOut,
  Settings,
  ChevronLeft,
  ChevronRight,
  Menu,
  Plane,
  BarChart3,
  User,
  Key,
  Activity,
  ArrowLeftRight,
  Gift,
  HelpCircle,
  AlertTriangle,
  FileText,
  CalendarDays,
  UserPlus,
  Sparkles,
  BookUser
} from 'lucide-react';
import { Button } from './ui/button';
import NotificationBell from './NotificationBell';

const Sidebar = ({ onToggle, featureToggles = {} }) => {
  const { user, logout, hasPermission, currentOrg, sidebarAccess } = useAuth();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleToggle = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    if (onToggle) onToggle(newState);
  };

  const allMenuItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard', testId: 'nav-dashboard', permission: 'view_dashboard', feature: null, accessKey: 'dashboard' },
    { path: '/my-schedule', icon: CalendarDays, label: 'My Schedule', testId: 'nav-my-schedule', permission: null, feature: null, accessKey: 'my_schedule' },
    { path: '/directory', icon: BookUser, label: 'Roster & Contacts', testId: 'nav-directory', permission: null, feature: 'directory', accessKey: null },
    { path: '/schedule', icon: Calendar, label: 'Schedule', testId: 'nav-schedule', permission: 'view_schedules', feature: 'schedules', accessKey: 'schedule' },
    { path: '/schedule-insights', icon: Sparkles, label: 'Schedule Insights', testId: 'nav-schedule-insights', permission: 'view_insights', feature: 'schedule_insights', accessKey: 'schedule_insights' },
    { path: '/shift-swaps', icon: ArrowLeftRight, label: 'Shift Exchange', testId: 'nav-shift-swaps', permission: null, feature: 'shift_swaps', accessKey: 'shift_swaps' },
    { path: '/pto', icon: Plane, label: 'PTO Management', testId: 'nav-pto', permission: 'approve_deny_pto', feature: 'pto_management', accessKey: 'pto' },
    { path: '/reports', icon: BarChart3, label: 'Reports', testId: 'nav-reports', permission: 'view_reports', feature: 'reports', accessKey: 'reports' },
    { path: '/admin', icon: Settings, label: 'Admin', testId: 'nav-admin', permission: 'system_settings', feature: null, accessKey: 'admin' },
    { path: '/team', icon: UserPlus, label: 'Team', testId: 'nav-team', permission: 'user_management', feature: null, accessKey: 'team' },
    { path: '/audit-log', icon: FileText, label: 'Audit Log', testId: 'nav-audit-log', permission: 'view_audit_log', feature: 'audit_log', accessKey: 'audit_log' },
    { path: '/time-tracking', icon: Clock, label: 'Time Tracking', testId: 'nav-time-tracking', permission: null, feature: 'time_tracking', accessKey: 'time_tracking' },
    { path: '/help', icon: HelpCircle, label: 'Help', testId: 'nav-help', permission: null, feature: null, accessKey: 'help' },
  ];

  const menuItems = allMenuItems.filter(item => {
    const permOk = item.permission === null || hasPermission(item.permission);
    const featureOk = item.feature === null || featureToggles[item.feature] !== false;
    // Sidebar access: admins always see everything. If sidebarAccess is set (array), restrict.
    // Items with accessKey === null (e.g. Roster & Contacts) are always visible, gated only by feature toggle.
    const sidebarOk = item.accessKey === null || user?.role === 'admin' || !sidebarAccess || sidebarAccess.includes(item.accessKey);
    return permOk && featureOk && sidebarOk;
  });

  const showNotifications = hasPermission('manage_swaps') || hasPermission('approve_deny_pto');

  const handleLogout = () => {
    logout();
  };

  return (
    <div className={`fixed left-0 top-0 h-full bg-white border-r border-gray-200 flex flex-col shadow-lg transition-all duration-300 ease-in-out ${
      isCollapsed ? 'w-16' : 'w-64'
    }`}>
      {/* Toggle Button */}
      <div className="absolute -right-3 top-6 z-10">
        <Button
          onClick={handleToggle}
          size="sm"
          variant="outline"
          className="h-8 w-8 p-0 bg-white border-2 border-gray-200 rounded-full shadow-md hover:shadow-lg"
          data-testid="sidebar-toggle"
        >
          {isCollapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Logo/Header */}
      <div className={`border-b border-gray-200 ${isCollapsed ? 'p-4' : 'p-6'}`}>
        <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'}`}>
          {isCollapsed ? (
            <div className="p-2 bg-blue-600 rounded-lg">
              <Calendar className="h-6 w-6 text-white" />
            </div>
          ) : (
            <img 
              src="/shiftela-logo.svg" 
              alt="Shiftela" 
              className="h-10"
              style={{ maxWidth: '180px' }}
            />
          )}
        </div>
        {!isCollapsed && currentOrg && (
          <div className="mt-2 text-xs text-gray-500 truncate" data-testid="sidebar-org-name">
            {currentOrg.name}
          </div>
        )}
      </div>

      {/* User Info */}
      <div className={`border-b border-gray-200 ${isCollapsed ? 'p-2' : 'p-4'}`}>
        <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
          <div className={`flex items-center ${isCollapsed ? '' : 'space-x-3'}`}>
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 font-semibold text-sm">
                {user?.username?.charAt(0).toUpperCase()}
              </span>
            </div>
            {!isCollapsed && (
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">{user?.username}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
              </div>
            )}
          </div>
          {!isCollapsed && showNotifications && (
            <NotificationBell />
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className={`flex-1 ${isCollapsed ? 'p-2' : 'p-4'}`}>
        <ul className="space-y-2">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  data-testid={item.testId}
                  className={`flex items-center rounded-lg transition-colors duration-200 relative group ${
                    isCollapsed ? 'justify-center p-3' : 'space-x-3 px-4 py-3'
                  } ${
                    isActive
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                  title={isCollapsed ? item.label : ''}
                >
                  <Icon className={`h-5 w-5 ${isActive ? 'text-blue-700' : 'text-gray-500'}`} />
                  {!isCollapsed && (
                    <span className="font-medium">{item.label}</span>
                  )}
                  
                  {/* Tooltip for collapsed state */}
                  {isCollapsed && (
                    <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                      {item.label}
                    </div>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Footer */}
      <div className={`border-t border-gray-200 ${isCollapsed ? 'p-2' : 'p-4'} space-y-2`}>
        {/* User Profile Section */}
        <Link
          to="/profile"
          className={`flex items-center w-full text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200 relative group ${
            isCollapsed ? 'justify-center p-3' : 'justify-start px-3 py-2'
          }`}
          title={isCollapsed ? 'My Account' : ''}
        >
          <User className="h-5 w-5" />
          {!isCollapsed && (
            <div className="ml-3 flex-1">
              <div className="text-sm font-medium">My Account</div>
              <div className="text-xs text-gray-500">{user?.username || user?.email}</div>
            </div>
          )}
          
          {/* Tooltip for collapsed state */}
          {isCollapsed && (
            <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
              My Account
            </div>
          )}
        </Link>

        <Button
          onClick={handleLogout}
          variant="ghost"
          className={`w-full text-gray-700 hover:text-red-600 hover:bg-red-50 relative group ${
            isCollapsed ? 'justify-center p-3' : 'justify-start'
          }`}
          data-testid="logout-button"
          title={isCollapsed ? 'Sign Out' : ''}
        >
          <LogOut className="h-5 w-5" />
          {!isCollapsed && <span className="ml-3">Sign Out</span>}
          
          {/* Tooltip for collapsed state */}
          {isCollapsed && (
            <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
              Sign Out
            </div>
          )}
        </Button>
      </div>
    </div>
  );
};

export default Sidebar;